import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AuthImage } from "../../components/AuthImage";
import { ReceiptPreview } from "../../components/ReceiptPreview";
import { useI18n } from "../../i18n";
import { EDUCATION } from "../../lib/constants";
import { usePlans } from "../../lib/plans";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";

const ResetPassword = ({ id }) => {
  const { t } = useI18n();
  const [pw, setPw] = useState("");
  const go = async () => {
    try { await api.put(`/admin/users/${id}/password`, { new_password: pw }); toast.success(t("adm_password_reset_ok")); setPw(""); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="flex items-end gap-3 rounded-xl border p-4">
      <div className="flex-1 space-y-1.5"><Label>{t("adm_reset_password")}</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="min. 6" data-testid="edit-reset-password-input" /></div>
      <button type="button" onClick={go} disabled={pw.length < 6} className="btn-ghost-gold !py-2" data-testid="edit-reset-password-btn">{t("adm_reset_btn")}</button>
    </div>
  );
};

const Sel = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1.5"><Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-${v}`}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const EditMemberDialog = ({ member, onClose, onSave }) => {
  const { t } = useI18n();
  const { plans, planName } = usePlans();
  const [f, setF] = useState({
    full_name: member.full_name, phone: member.phone, gender: member.gender, education: member.education, package: member.package,
    bank_name: member.bank_name, bank_account_number: member.bank_account_number, bank_account_name: member.bank_account_name,
    status: member.status, payment_status: member.payment_status, financial_support: member.financial_support, admin_note: member.admin_note || "",
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const text = (k, label, extra = {}) => (
    <div className="space-y-1.5"><Label>{label}</Label><Input value={f[k]} onChange={set(k)} data-testid={`edit-${k}-input`} {...extra} /></div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" aria-describedby={undefined} data-testid="edit-member-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{t("adm_edit_member")}</DialogTitle></DialogHeader>
        <div>
          <p className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">{t("adm_documents")}</p>
          <div className="grid grid-cols-3 gap-4">
            <div><p className="mb-1 text-xs">{t("f_photo_upload")}</p><AuthImage path={member.photo_path} className="h-40 w-full rounded-xl" testId="edit-photo-preview" /></div>
            <div><p className="mb-1 text-xs">{t("f_id_upload")}</p><AuthImage path={member.id_doc_path} className="h-40 w-full rounded-xl" testId="edit-id-preview" /></div>
            <div><p className="mb-1 text-xs">{t("pay_receipt")}</p><ReceiptPreview path={member.receipt_path} className="h-40 w-full" testId="edit-receipt-preview" /></div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{t("adm_biometric")}: {member.biometric_credential_id ? t("adm_yes") : t("adm_no")} · {t("adm_registered")}: {new Date(member.created_at).toLocaleDateString()}
            {(member.payment_method || member.payment_reference) && <> · {t("pay_method")}: <span className="font-mono">{member.payment_method} {member.payment_reference}</span></>}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {text("full_name", t("f_full_name"))}
          {text("phone", t("f_phone"))}
          <Sel label={t("f_gender")} value={f.gender} onChange={set("gender")} options={["male", "female", "other"].map((g) => [g, t(`g_${g}`)])} testId="edit-gender" />
          <Sel label={t("f_education")} value={f.education} onChange={set("education")} options={EDUCATION.map((e) => [e, t(`edu_${e}`)])} testId="edit-education" />
          {text("bank_name", t("f_bank_name"))}
          {text("bank_account_number", t("f_account_number"))}
          {text("bank_account_name", t("f_account_name"))}
          <Sel label={t("adm_package")} value={f.package} onChange={set("package")} options={plans.map((p) => [p.key, planName(p.key)])} testId="edit-package" />
          <Sel label={t("adm_status")} value={f.status} onChange={set("status")} options={["pending", "approved", "rejected", "blocked"].map((s) => [s, t(`st_${s}`)])} testId="edit-status" />
          <Sel label={t("adm_payment")} value={f.payment_status} onChange={set("payment_status")} options={["unpaid", "submitted", "paid", "rejected"].map((s) => [s, t(`pay_${s}`)])} testId="edit-payment-status" />
          {text("financial_support", t("adm_support"), { type: "number", min: 0 })}
        </div>
        <div className="space-y-1.5"><Label>{t("adm_note")}</Label><Textarea value={f.admin_note} onChange={set("admin_note")} data-testid="edit-admin-note-input" /></div>
        <ResetPassword id={member.id} />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost-gold !py-2" data-testid="edit-cancel-btn">{t("adm_cancel")}</button>
          <button onClick={() => onSave({ ...f, financial_support: Number(f.financial_support) || 0 })} className="btn-gold !py-2" data-testid="edit-save-btn">{t("adm_save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
